/**
 * Timestamp formatting for the console.
 *
 * A bare `toLocaleString()` inherits the browser locale. On an Arabic (RTL)
 * locale the bidi algorithm reorders the parts of the string and the result is
 * unreadable in an LTR table — e.g. `15ص 3:22:25 -2026/9/`. The value below is
 * still the viewer's local time; only the layout is pinned to a stable pattern.
 */
const STAMP: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

const DAY: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

function parse(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `2026-09-15 12:22:25` in the viewer's local time zone. */
export function formatTimestamp(value: string | number | Date | null | undefined): string {
  const date = parse(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-GB", STAMP).format(date).replace(",", "");
}

/** `2026-09-15` in the viewer's local time zone. */
export function formatDate(value: string | number | Date | null | undefined): string {
  const date = parse(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-GB", DAY).format(date);
}

/** Relative age for "last seen"-style fields; falls back to the absolute stamp. */
export function formatAge(value: string | number | Date | null | undefined): string {
  const date = parse(value);
  if (!date) return "—";
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 2_592_000) return `${Math.round(seconds / 86_400)}d ago`;
  return formatDate(date);
}
